/* eslint-disable no-console */
import { ButtonType, ButtonWidget, DialogType, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import { Formik, useFormikContext } from 'formik';
import React from 'react';

import { useSettingsCoordinator } from '~settings-coordinator/SettingsCoordinatorHook';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { OAuthCredentialsService } from '~src/connectivity/services/OAuthCredentialsService';

import { Messages } from '../../../../../codegen/Localize';
import { ModalInstance, ReactModalService } from '../../../../../commonInfra/dialogs/ReactModalService';
import { AddOAuthClientParams, AddOAuthClientsRequest } from '../../../../../connectivity/serverApi/requests/OAuth/AddOAuthClientsRequest';
import { DeleteOAuthClientsRequest } from '../../../../../connectivity/serverApi/requests/OAuth/DeleteOAuthClientsRequest';
import { GetOAuthClientsRequest, IGetOAuthClientsResult } from '../../../../../connectivity/serverApi/requests/OAuth/GetOAuthClientsRequest';
import { IOAuthClient } from '../../../../../connectivity/serverApi/types/OAuth/IOAuthClient';
import { OAuthCredentialTypes } from '../../../../../connectivity/serverApi/types/OAuth/IOAuthCredential';
import { IResult } from '../../../../../httpClient/serverApi/types/IResponse';
import WebHttpClient from '../../../../../httpClient/WebHttpClient';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { learnMoreStyle } from '../../../../styles/CommonStyles';
import { addOAuthClientDialogStyle, buttonStyle } from '../../../styles/oauth-client/OAuthClientsRegistryStyles';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import {
  AddOAuthClientDialog,
  AddOAuthClientDialogProps,
  AddOAuthClientDialogState,
  OAuthClientSetting,
  OAuthClientsFormField,
  OAuthClientsFormikProps
} from './AddOAuthClientDialog';

interface AddOAuthClientResult {
  id: string;
  errorMessage: string;
}
interface AddOAuthClientsResult {
  addOAuthClientsResult: AddOAuthClientResult[];
}

function getDescription() {
  return <>
    <span data-tb-test-id='oauth-client-registry-description'>
      {Messages.oauthClientsRegistryDescription()}
      <TextLinkWidget
        testId='oauth-client-registry-learn-more'
        className={learnMoreStyle}
        kind={TextLinkKind.Standard}
        href={HelpService.getHelpUrlByCategory('server-oauth-registry')}
        target={'_blank'}
        rel={'noreferrer noopener'}
      >
        {Messages.learnMore()}
      </TextLinkWidget>
    </span>
  </>;
}

export function renderOAuthClientListTableHeader(isSlackOAuthClient = false, isMsTeamsOAuthClient = false) {
  const tableHeaders = isSlackOAuthClient || isMsTeamsOAuthClient ?
    { [Messages.oauthClientDbClass()]: '', [Messages.oauthClientId()]: '', '': '' } :
    { [Messages.oauthClientDbClass()]: '', [Messages.oauthClientProvider()]: '', [Messages.oauthClientInstanceUrl()]: '', [Messages.oauthClientId()]: '', [Messages.fileBased()]: '', '': '' };
  let header = Object.keys(tableHeaders);
  return header.map((key, index) => {
    return (<th key={index} className='tb-data-grid-headers-line'>
      <span className='tb-data-grid-header-text'>{key}</span>
    </th>);
  });
}


export async function fetchOAuthClients(): Promise<IGetOAuthClientsResult> {
  return WebHttpClient.sendRequest(new GetOAuthClientsRequest({}));
}

export async function deleteOAuthClients(toDeleteIds: string[]): Promise<IResult> {
  return WebHttpClient.sendRequest(new DeleteOAuthClientsRequest(toDeleteIds));
}

export async function addOAuthClients(toAddClients: AddOAuthClientParams[]): Promise<IResult> {
  return WebHttpClient.sendRequest(new AddOAuthClientsRequest({ oauthClients: toAddClients }));
}

/**
 * Component with dependencies injected as props
 */
export function OAuthClientsRegistrySection() {
  const [oauthClients, setOAuthClients] = React.useState<OAuthClientSetting[]>([]);
  const initialValues = React.useMemo(() => {
    return { oauthClients };
  }, [oauthClients]);

  React.useEffect(() => updateOAuthClients(), []);

  function updateOAuthClients() {
    fetchOAuthClients().then((result: { oauthClients: IOAuthClient[] }) => {
      const clientSettings = result.oauthClients
      // llm-gateway clients are managed by the Edit LLM Connection dialog.
      .filter((v: IOAuthClient) => v.type !== OAuthCredentialTypes.LlmGateway)
      .map((v: IOAuthClient) => {
        const clientSetting: OAuthClientSetting = {
          dbClass: v.type,
          instanceUrl: v.instanceUrl ?? '',
          oauthClientId: v.clientId,
          oauthClientSecret: '',
          oauthClientRedirectUri: '',
          oauthConfigId: v.oauthConfigId ?? '',
          oauthConfigContent: v.oauthConfigContent ?? '',
          fileName: v.oauthConfigFileName ?? '',
          configLabel: v.configLabel ?? '',
          id: v.id
        };
        return clientSetting;
      });
      setOAuthClients(clientSettings);
    });
  }

  const handleSaveAction = React.useCallback(async (values: OAuthClientsFormField, formik: OAuthClientsFormikProps) => {
    let toAddClients: AddOAuthClientParams[] = [];
    let origIds = initialValues.oauthClients.filter((client: OAuthClientSetting) => client.id !== '').map((client: OAuthClientSetting) => client.id);
    values.oauthClients
      .filter((client: OAuthClientSetting) => !origIds.includes(client.id))
      .map((client: OAuthClientSetting) => {
        toAddClients.push({
          'dbClass': client.dbClass,
          'instanceUrl': client.instanceUrl,
          'clientId': client.oauthClientId,
          'clientSecret': client.oauthClientSecret,
          'redirectUri': client.oauthClientRedirectUri,
          'oauthConfigId': client.oauthConfigId,
          'oauthConfigContent': client.oauthConfigContent,
          'oauthConfigFileName': client.fileName,
          'configLabel': client.configLabel
        });
      });

    const promises: Promise<IResult>[] = [];

    // the difference between initial id set and the current one are the ids to be deleted
    let allCurrentIds = values.oauthClients.filter((client: OAuthClientSetting) => client.id !== '').map((client: OAuthClientSetting) => client.id);
    let toDeleteIds = origIds.filter((id: string) => allCurrentIds.indexOf(id) < 0);

    if (toAddClients.length) {
      const addPromise = addOAuthClients(toAddClients);
      promises.push(addPromise);
    }
    if (toDeleteIds.length) {
      const delPromise = deleteOAuthClients(toDeleteIds);
      promises.push(delPromise);
    }

    if (promises.length) {
      try {
        const result = await Promise.all(promises);
        const addClientResults = result[0] as AddOAuthClientsResult;
        const failedAdd = addClientResults.addOAuthClientsResult?.filter((x: AddOAuthClientResult) => x.id === '-1');
        if (failedAdd?.length) {
          const errMsgs = failedAdd.map(x => x.errorMessage);
          throw errMsgs;
        }
      } finally {
        updateOAuthClients();
      }
    }
  }, [initialValues.oauthClients]);

  return (
    <SettingsSection title={Messages.oauthClientsRegistry()}
      description={getDescription()}
      testId='oauth-clients-registry-site-setting'>
      <div>
        <Formik initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={(values: OAuthClientsFormField, formik: OAuthClientsFormikProps) => handleSaveAction(values, formik)}>
          <FormContent />
        </Formik>
      </div>
    </SettingsSection>
  );
}

function useAddOAuthClientSubmit() {
  const formik = useFormikContext<OAuthClientsFormField>();
  return React.useCallback(
    (client: AddOAuthClientDialogState) => {
      const clients = formik.values.oauthClients.filter((c: OAuthClientSetting) => (c.dbClass !== client.dbClass || c.oauthConfigId !== client.oauthConfigId || c.instanceUrl !== client.instanceUrl || c.oauthClientId !== client.oauthClientId));
      clients.push({
        dbClass: client.dbClass,
        instanceUrl: client.instanceUrl,
        oauthClientId: client.oauthClientId,
        oauthClientSecret: client.oauthClientSecret,
        oauthClientRedirectUri: client.oauthClientRedirectUri,
        oauthConfigId: client.oauthConfigId,
        configLabel: client.configLabel,
        oauthConfigContent: client.oauthConfigContent,
        fileName: client.fileName,
        id: Math.random().toString(36).substring(2)
      });
      formik.setFieldValue('oauthClients', clients, false);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik, formik.setFieldValue]
  );
}

export function useOpenAddOAuthClientDialog() {
  const formik = useFormikContext<OAuthClientsFormField>();
  const handleAddOAuthClientSubmit = useAddOAuthClientSubmit();
  return React.useCallback(
    (): Promise<IResult> => {
      let modalInstance: ModalInstance;
      const dialogProps: AddOAuthClientDialogProps = {
        dismiss: () => modalInstance && modalInstance.dismiss(),
        close: (result: any) => modalInstance && modalInstance.close(result),
        addOAuthClientSubmit: handleAddOAuthClientSubmit,
        showInstanceUrlSection: true,
      };

      modalInstance = ReactModalService.open({
        type: DialogType.Uber,
        dialogContent: React.createElement(AddOAuthClientDialog, dialogProps),
        testId: 'addOAuthClientDialog',
        showCloseButton: true,
        dialogStyle: addOAuthClientDialogStyle,
        shouldNotSetDefaultWidth: true
      });
      return modalInstance.result;
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik]
  );
}

export function FormContent() {
  useSettingsCoordinator();
  const formik = useFormikContext<OAuthClientsFormField>();
  const handleOpenAddOAuthClientDialog = useOpenAddOAuthClientDialog();

  return <>
    <div>
      <ButtonWidget buttonType={ButtonType.Outline}
        testId='add-oauth-client-on-site-button'
        className='tb-outline-button tb-margin-left-wide'
        onClick={() => handleOpenAddOAuthClientDialog()}
        disabled={false}
        type='button'>{Messages.addOAuthClient_action()}
      </ButtonWidget>
    </div>
    <p />
    {(formik.values.oauthClients.length > 0 || formik.dirty) &&
      <div>
        <table id='oauth-clients-list-table-id' className='tb-static-grid-table tb-static-grid-table-settings-width'>
          <thead>
            <tr data-test-id='create-oauth-client-db-class'>
              {renderOAuthClientListTableHeader()}
            </tr>
          </thead>
          <tbody id='oauth-clients-list-body-id'>
            {formik.values.oauthClients.map((client: OAuthClientSetting) => {
              const props: TableRowProps = {
                client,
                deleteClient: (client: OAuthClientSetting) => { handleDeleteClient(client, formik); },
                isSlackOAuthClient: false,
                isMsTeamsOAuthClient: false,
              };
              if (OAuthCredentialsService.getDisplayStrings(client.dbClass)){
                return renderOAuthClientTableRow(props, false, false);
              }
            })}
          </tbody>
        </table>
      </div>
    }
  </>;
}

export function handleDeleteClient(client: OAuthClientSetting, formik: OAuthClientsFormikProps) {
  const oauthClients = formik.values.oauthClients;
  const index = oauthClients.indexOf(client, 0);
  if (index > -1) {
    const clients = [...oauthClients];
    clients.splice(index, 1);
    formik.setFieldValue('oauthClients', clients, false);
  }
}

interface TableRowProps {
  client: OAuthClientSetting;
  deleteClient: (client: OAuthClientSetting) => void;
  isSlackOAuthClient?: boolean;
  isMsTeamsOAuthClient?: boolean;
}

export function renderOAuthClientTableRow(props: TableRowProps, emptyState: boolean, deleteButtonDisabled: boolean, emptyStateMessage?: string) {
  return (
    <tr key={props.client ? props.client.id : 'empty-state'}>
      {/* DB Class Column */}
      <td>
        <span id='oauth-client-dbclass' className='tb-data-grid-cell-text tb-lr-padded-wide'>
          {emptyState ? emptyStateMessage : OAuthCredentialsService.getDisplayStrings(props.client.dbClass)?.displayName}
        </span>
      </td>
      {/* OAuth Type / OAuth Config ID column */}
      {!emptyState && !props.isSlackOAuthClient && !props.isMsTeamsOAuthClient &&
        <td>
          <span id='oauth-config-id' className='tb-data-grid-cell-text tb-lr-padded-wide'>{props.client.configLabel || (props.client.oauthConfigId || Messages.none())}</span>
        </td>
      }
      {/* Instance URL Column */}
      {!emptyState && !props.isSlackOAuthClient && !props.isMsTeamsOAuthClient &&
        <td>
          <span className='tb-data-grid-cell-text tb-lr-padded-wide'>
            {props.client.instanceUrl !== '' ? props.client.instanceUrl : Messages.none()}
          </span>
        </td>
      }
      {/* Client ID Column */}
      {!emptyState &&
        <td>
          <span className='tb-data-grid-cell-text tb-lr-padded-wide'>
            {props.client.oauthClientId}
          </span>
        </td>
      }
      {/* File Name Column */}
      {!emptyState && !props.isSlackOAuthClient && !props.isMsTeamsOAuthClient &&
        <td>
          <span id='oauth-config-file' className='tb-data-grid-cell-text tb-lr-padded-wide'>
            {props.client.fileName || Messages.none()}
          </span>
        </td>
      }
      {/* Delete Button Column */}
      {!emptyState &&
        <td>
          <span className='tb-data-grid-cell-text tb-lr-padded-wide'>
            <ButtonWidget buttonType={ButtonType.LowEmphasis}
              testId='delete-oauth-client-on-site-button'
              className={buttonStyle}
              onClick={() => props.deleteClient(props.client)}
              disabled={deleteButtonDisabled}
              type='button'>{Messages.oauthDeleteClient()}
            </ButtonWidget>
          </span>
        </td>
      }
    </tr>
  );
}

export const OAuthClientsRegistrySectionSpecs = componentRegistryEntry({
  name: 'OAuthClientsRegistrySection',
  component: OAuthClientsRegistrySection
});
